import { create } from 'zustand';

interface ChatLauncherState {
	/**
	 * Monotonic counter — bumped each time the BottomNav's Chat tab (or any
	 * other surface) wants to open the chat. The HelpChatWidget watches this
	 * value and runs its `launchChat()` flow when it changes. Counter rather
	 * than a boolean so back-to-back taps after the user dismisses the panel
	 * still trigger a fresh open without an interim toggle.
	 */
	openRequestVersion: number;
	requestOpen: () => void;
	/**
	 * True when the user has an in-flight conversation the widget knows
	 * about (either discovered on bootstrap or started this session and
	 * not yet resolved). Drives the "active" dot on the BottomNav Chat
	 * tab + desktop FAB so visitors can tell at a glance whether tapping
	 * resumes an existing thread or starts a new one — without us
	 * auto-opening the panel and hijacking their attention on every page
	 * load (the previous behavior was hostile on mobile, where the
	 * panel goes full-screen).
	 */
	hasActiveConversation: boolean;
	setHasActiveConversation: (value: boolean) => void;
	/**
	 * Count of inbound rep messages the user hasn't seen yet. Bumped by
	 * the widget when an MQTT message arrives while the panel is closed,
	 * cleared the moment the panel opens (the user is now actively
	 * looking at the conversation, so any catch-up they read counts as
	 * read for badge purposes).
	 *
	 * Rendered on the Chat tab + FAB the same way the cart badge
	 * renders qty: `1`, `2`, ..., `9+` for >=10. A non-zero count
	 * outranks the active-only dot — both are signals to "tap me", but
	 * unread counts get the louder treatment.
	 */
	unreadCount: number;
	bumpUnread: () => void;
	clearUnread: () => void;
	/** Reset everything (called when the user signs out). */
	reset: () => void;
}

/**
 * Lightweight imperative bridge for the chat panel. The HelpChatWidget
 * owns the actual chat state machine (closed → starting → live →
 * unavailable) and the launchChat side-effect (POST /start, fetch context),
 * so we don't try to lift all of that here. Instead, this store exposes:
 *
 *   - `requestOpen()`: surfaces (BottomNav tab on mobile, FAB on desktop,
 *     future "Talk to a rep" CTAs) ask the widget to open without
 *     prop-drilling a callback through PortalLayout.
 *   - `hasActiveConversation` + `unreadCount`: the widget pushes its
 *     idle-state metadata up here so launcher surfaces can render badges
 *     without each one re-fetching `/current`.
 */
export const useChatLauncherStore = create<ChatLauncherState>((set, get) => ({
	openRequestVersion: 0,
	requestOpen: () => set({ openRequestVersion: get().openRequestVersion + 1 }),
	hasActiveConversation: false,
	setHasActiveConversation: (value) => set({ hasActiveConversation: value }),
	unreadCount: 0,
	bumpUnread: () => set({ unreadCount: get().unreadCount + 1 }),
	clearUnread: () => set({ unreadCount: 0 }),
	reset: () =>
		set({
			openRequestVersion: 0,
			hasActiveConversation: false,
			unreadCount: 0,
		}),
}));
